import format from 'date-fns/format';

import { Api } from '../api/Api';
import {
  AddCtnRequest,
  BasketItemRequestModel,
  CreateOrUpdateOrderRequestItemDetails,
  DeliveryUpdateDataAddressModel,
  DeliveryUpdateDataModel,
  ProductPromotionItemModel,
  RequestModel,
  RequestModel2,
  ValidateCtnRequest,
  ValidateCtnV2Request,
} from '../api/data-contracts';
import {
  BasketItemDetails,
  BasketItemType,
  ExtendedBasketItemRequestModel,
  ExtendedBasketItemResponseModel,
  ExtendedBasketRequestModel,
  SubscriberType,
} from './basket';
import { DeliverySlug } from './delivery';
import { ExtendedRequestClientInfoModel, ExtendedRequestModel6 } from './order';

const VIRTUAL_POINT_CODE = '0000000000000000';

const normalizeDate = (date: string | Date) => {
  try {
    const newDate = new Date(date);
    return format(newDate, 'yyyy-MM-dd');
  } catch (err) {
    return '';
  }
};

// eslint-disable-next-line consistent-return
const getDiscount = (price: number, oldPrice?: number) => {
  if (oldPrice) {
    return Math.floor(((oldPrice - price) * 100) / oldPrice);
  }
};

export const normalizeCtn = (value: string = '') => value?.replace(/^(\+7)/, '');

type CourierUpdateData = Pick<
  DeliveryUpdateDataAddressModel,
  'city' | 'cityId' | 'street' | 'flat'
> &
  Pick<DeliveryUpdateDataModel, 'timeFrom' | 'timeTo' | 'price' | 'date'>;

/**
 * Хелперы
 */
export class Helpers {
  currentRegion: string;

  source: string | undefined;

  constructor(currentRegion: string, source?: string) {
    this.currentRegion = currentRegion;
    this.source = source;
  }

  createTariffItem = (
    tariffId: string,
    subscriberType: BasketItemDetails['subscriberType'] = SubscriberType.New,
  ): ExtendedBasketItemRequestModel => ({
    details: { subscriberType },
    id: tariffId,
    isExcluded: false,
    itemType: BasketItemType.Tariff,
  });

  createServiceItem = (
    id: string,
    parentItemId: string,
    quantity = 1,
  ): ExtendedBasketItemRequestModel => ({
    id,
    parentItemId,
    isExcluded: false,
    itemType: BasketItemType.Service,
    quantity,
  });

  createSberServiceItem = (
    parentItemId: string,
    details: {
      insurancePrice: number;
      insuranceType: string;
      insurancePeriodId: string;
      insuranceProductId: string;
      serviceType: string;
      isSberInsurance: boolean;
      serviceData: {
        period: string;
        periodId: string;
      };
    },
  ): BasketItemRequestModel => ({
    isExcluded: false,
    itemType: BasketItemType.Service,
    quantity: 1,
    parentItemId,
    details,
  });

  createCtnItem = (
    ctn: string,
    subscriberType: BasketItemDetails['subscriberType'] = SubscriberType.New,
    parentItemId?: string,
  ): ExtendedBasketItemRequestModel => ({
    details: { ctn, subscriberType },
    isExcluded: false,
    itemType: BasketItemType.Ctn,
    parentItemId,
  });

  createTariffBundle = (
    tariffId: string | number,
    ctn: string,
    subscriberType: BasketItemDetails['subscriberType'] = SubscriberType.New,
  ): ExtendedBasketItemRequestModel => ({
    ...this.createTariffItem(String(tariffId), subscriberType),
    children: [this.createCtnItem(ctn, subscriberType)],
  });

  createRequestItemFromResponseItem = (
    item: ExtendedBasketItemResponseModel,
  ): ExtendedBasketItemRequestModel => ({
    details: item.details,
    id: item.id,
    isExcluded: item.isExcluded,
    itemId: item.itemId,
    itemType: item.itemType,
    parentItemId: item.parentItemId,
    quantity: item.quantity,
    source: item.source,
  });

  createBasketData = (
    items: ExtendedBasketRequestModel['items'],
    regionPath?: ExtendedBasketRequestModel['regionPath'],
    ctn?: string,
  ): ExtendedBasketRequestModel => ({
    items,
    // FIXME в типах нет ctn, узнать это мы по ошибке передаём или в типах он должен быть
    // @ts-expect-error в типах нет ctn
    ctn,
    regionPath: regionPath || this.currentRegion,
  });

  /**
   * Фильтрация деталей товара т.к. не все поля допустимы в запросе
   */
  filterDetailsForRequest = (details?: BasketItemDetails): BasketItemDetails => {
    const newDetails = { ...details };
    delete newDetails.isFamily;
    delete newDetails.familyMinSimCount;
    delete newDetails.familyMaxSimCount;
    delete newDetails.brandName;
    delete newDetails.variant;
    delete newDetails.mainCollectionId;
    delete newDetails.mainCollectionName;
    delete newDetails.parentCollectionId;
    delete newDetails.parentCollectionName;

    return newDetails;
  };

  /**
   *
   */
  configureDetails = (details?: BasketItemDetails): BasketItemDetails => {
    return { ...details };
  };

  /**
   *
   */
  createRequestItem = (
    item: ExtendedBasketItemResponseModel,
    newItem: Partial<ExtendedBasketItemResponseModel>,
  ): ExtendedBasketItemRequestModel =>
    this.createRequestItemFromResponseItem({
      ...item,
      ...newItem,
      children: [...(newItem.children || item.children || [])],
      details: {
        ...this.filterDetailsForRequest(newItem.details),
      },
    });

  /**
   *
   */
  createOrderItemDetails = (
    items?: ExtendedBasketItemResponseModel[],
  ): CreateOrUpdateOrderRequestItemDetails[] =>
    items?.map((item) => ({ itemId: item.itemId, details: item.details })) || [];

  /**
   *
   */
  createGetTariffsRequestData = (socs: string[]): Parameters<Api['tariffsGetTariffs']>[0] => {
    return {
      region: this.currentRegion,
      socs: socs.join(','),
    };
  };

  createAuthMobileIdLoginHint = (ctn: string): string => {
    return `MSISDN:${ctn}`;
  };

  createAuthMobileIdData = (ctn: string): Parameters<Api['mobileIdAuthorize']>[0] => ({
    login_hint: this.createAuthMobileIdLoginHint(ctn),
  });

  prepareNiceClasses = (arr?: string[]) => {
    if (!arr?.length) return '';

    const chunkSize = 4;
    const result = [];

    for (let i = 0; i < arr.length; i += chunkSize) {
      const chunk = arr.slice(i, i + chunkSize);
      result.push(chunk.join(''));
    }

    return result.join(',');
  };

  createCtnData = (
    number: string,
    niceClasses?: string[],
  ): Parameters<Api['ctnGetCtnList']>[0] => ({
    Region: this.currentRegion,
    Number: number,
    NiceClasses: this.prepareNiceClasses(niceClasses),
  });

  createContactData = ({
    ctn,
    clientType = 'b2c',
    ...data
    // @ts-expect-error в сгенерированных типах обязательным является regionId, хотя это не так
    // TODO передать правки на .Net
  }: { ctn: string } & Omit<ExtendedRequestClientInfoModel, 'phone'>): ExtendedRequestModel6 => ({
    client: {
      ...data,
      clientType,
      phone: normalizeCtn(ctn),
    },
  });

  createValidateMnpCtnData = (phone: ValidateCtnRequest['phone_number']): ValidateCtnRequest => ({
    phone_number: phone,
    regionPath: this.currentRegion,
    subscriber_type: 'mnp',
  });

  createValidateMnpCtnV2Data = (
    phone: ValidateCtnRequest['phone_number'],
  ): ValidateCtnV2Request => ({
    phone_number: phone,
    regionPath: this.currentRegion,
    subscriber_type: 'mnp',
  });

  createAddCtnData = (data: AddCtnRequest): AddCtnRequest => ({
    ...data,
    // @ts-expect-error в типах нет source, но мы зачем-то отправляем, разобраться
    source: this.source,
  });

  createRegisterPaymentData = (
    data: Partial<RequestModel2> & Required<Pick<RequestModel2, 'basketId' | 'phone'>>,
  ): RequestModel => ({
    // @ts-expect-error в типах нет processUrl, но мы зачем-то отправляем
    processUrl: null,
    offersAccepted: true,
    isPreOrder: false,
    preOrderSlug: 'sim',
    ...data,
  });

  createSendTokenData = (
    basketId: string,
    ctn: string,
  ): Parameters<Api['creditsSendToken']>[0] => ({
    basketId,
    ctn,
    template: 'credit',
  });

  createValidateTokenData = (
    basketId: string,
    ctn: string,
    token: string,
  ): Parameters<Api['creditsValidateToken']>[0] => ({
    ...this.createSendTokenData(basketId, ctn),
    token,
  });

  createGetTariffsData = ({ isMnp = null, activeOnly = false } = {}): Parameters<
    Api['tariffsGetTariffs2']
  >[0] => ({
    Region: this.currentRegion,
    IsMnp: isMnp,
    ActiveOnly: activeOnly,
  });

  createVirtualPoint = (): DeliveryUpdateDataModel => ({
    type: DeliverySlug.Takeaway,
    pointCode: VIRTUAL_POINT_CODE,
    date: normalizeDate(new Date()),
    price: 0,
  });

  createFastDelivery = ({
    date,
    city,
    cityId,
    street = 'EmptyStreet',
    flat,
    price,
    timeTo,
  }: Pick<DeliveryUpdateDataAddressModel, 'city' | 'cityId' | 'street' | 'flat'> &
    Pick<DeliveryUpdateDataModel, 'date' | 'price' | 'timeTo'>): DeliveryUpdateDataModel => ({
    type: DeliverySlug.FastMovement,
    date: date ? normalizeDate(date) : '',
    address: {
      city,
      cityId,
      street,
      flat,
    },
    price,
    timeTo,
  });

  createCourier = ({
    date,
    city,
    cityId,
    street = 'EmptyStreet',
    flat,
    price,
    timeFrom,
    timeTo,
  }: CourierUpdateData): DeliveryUpdateDataModel => ({
    type: DeliverySlug.Courier,
    date: date ? normalizeDate(date) : '',
    address: {
      city,
      cityId,
      street,
      flat,
    },
    price,
    timeFrom,
    timeTo,
  });

  createRecommendedDevices = (products: ProductPromotionItemModel[]) =>
    products.map((product) => {
      const isSamePrice = product.price === product.oldPrice;
      const discount = getDiscount(product.price, product.oldPrice);

      return {
        id: product.id,
        slug: product.url,
        imgOptions: {
          src: product.imageUrl,
          alt: product.productName,
        },
        name: product.productName,
        link: product.url,
        price: product.price,
        oldPrice: isSamePrice ? undefined : product.oldPrice,
        discount: isSamePrice ? undefined : discount,
      };
    });
}
